// lecture.tsx — record a lecture → live on-device transcript → the material box (§5b build,
// 2026-08-18: the leader's most-loved flow, which their update broke — 50 counted complaints).
//
// Long-form sibling of chat/voice.tsx's MicButton: the SAME device speech engine, run CONTINUOUSLY.
// Speech sessions end on their own every minute or so (engine behaviour, not an error), so while the
// user is still recording we re-arm on every `onend` and stitch the finalized segments together.
// Nothing audio reaches Oriora — the device's own speech engine does the transcription; the finished
// TRANSCRIPT goes wherever pasted text goes (one Digest call, same as OCR). Where the API is absent
// (older iOS webview) the button self-hides, exactly like MicButton.
import { useEffect, useRef, useState } from "react";
import { Mic, Square } from "lucide-react";

function getSR(): (new () => any) | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
}

export function lectureAvailable(): boolean { return !!getSR(); }

export function LectureRecorder({ onTranscript, disabled }: {
  onTranscript: (text: string) => void;   // called with each FINALIZED chunk (append as it lands)
  disabled?: boolean;
}) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [interim, setInterim] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const recRef = useRef<any>(null);
  const wantRef = useRef(false);          // the user's intent — survives engine restarts
  const restartsRef = useRef(0);          // rapid-failure guard: engine dying instantly ≠ session end
  const lastStartRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => { wantRef.current = false; try { recRef.current?.stop(); } catch { /* unmount */ } if (timerRef.current) clearInterval(timerRef.current); }, []);

  const SR = getSR();
  if (!SR) return null;

  function arm() {
    const rec = new SR!();
    rec.lang = navigator.language || "en-US";
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onresult = (e: any) => {
      let interimText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        const t = r?.[0]?.transcript ?? "";
        if (r.isFinal && t.trim()) onTranscript(t.trim());
        else interimText += t;
      }
      setInterim(interimText.trim());
    };
    rec.onerror = (e: any) => {
      // Permission errors are terminal; 'no-speech'/'network' blips are not — onend re-arms.
      if (e?.error === "not-allowed" || e?.error === "service-not-allowed") {
        wantRef.current = false;
        setNote("Microphone access is off — allow it in Settings to record.");
      }
    };
    rec.onend = () => {
      setInterim("");
      if (!wantRef.current) { setRecording(false); return; }
      // The engine ends sessions periodically by design; re-arm. But if it dies within ~2s
      // repeatedly, something is actually wrong — stop rather than loop forever.
      if (Date.now() - lastStartRef.current < 2000) {
        restartsRef.current += 1;
        if (restartsRef.current >= 3) {
          wantRef.current = false; setRecording(false);
          setNote("The device's speech engine keeps stopping — your transcript so far is kept below.");
          return;
        }
      } else restartsRef.current = 0;
      try { lastStartRef.current = Date.now(); rec.start(); }
      catch { try { const r2 = arm(); lastStartRef.current = Date.now(); r2.start(); recRef.current = r2; } catch { wantRef.current = false; setRecording(false); } }
    };
    return rec;
  }

  function start() {
    setNote(null); setSeconds(0); restartsRef.current = 0;
    try {
      const rec = arm();
      recRef.current = rec;
      wantRef.current = true;
      lastStartRef.current = Date.now();
      rec.start();
      setRecording(true);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch { setNote("Couldn't start recording here — dictate with the keyboard mic or paste the text."); }
  }

  function stop() {
    wantRef.current = false;
    try { recRef.current?.stop(); } catch { /* already stopped */ }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false); setInterim("");
  }

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <>
      <button type="button" className="btn btn-sm btn-pill row" style={{ gap: "0.35rem", ...(recording ? { background: "var(--accent)", borderColor: "var(--accent)", color: "#fff" } : {}) }}
        disabled={disabled} onClick={recording ? stop : start}
        aria-label={recording ? "Stop recording" : "Record the lecture"}>
        {recording ? <Square size={13} /> : <Mic size={13} />} {recording ? `Recording ${mm}:${ss} — tap to stop` : "Record the lecture"}
      </button>
      {recording && (
        <div className="muted" style={{ fontSize: "0.8rem", width: "100%" }} aria-live="polite">
          {interim ? `…${interim.slice(-120)}` : "Listening — the transcript lands below as it firms up."}
        </div>
      )}
      {note && <div className="muted" style={{ fontSize: "0.8rem", width: "100%" }}>{note}</div>}
    </>
  );
}
